/**
 * Portal navigation editor (improvement #1) — lives in the editor's Portal
 * settings → Navigation section. Edits `portal_config.nav`, the EXPLICIT,
 * author-built portal sidebar: an ordered list of entries, each a **page** link
 * (pick from the app's pages), an **external link** (label + url), or a
 * **group** (label + child page/links, one level deep). Nothing is automatic —
 * an empty list means an empty sidebar; "Add all pages" is a one-click
 * *explicit* convenience.
 *
 * Visual model: lightweight horizontal rows (a kind glyph + inline fields +
 * hover-revealed reorder/remove), groups as a soft framed section with indented
 * children — a clean form-builder feel rather than nested heavy cards.
 */
import { ArrowDown, ArrowUp, FileText, FolderPlus, Link2, Plus, Trash2 } from "lucide-react";
import { type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import type { PortalNavConfigItem } from "@/lib/api-apps";
import { navIcon, NAV_ICON_NAMES } from "@/lib/portalNavIcons";

export interface NavEditorPage {
  slug: string;
  title: string;
}

const FIELD_CLS =
  "h-8 min-w-0 rounded-md border border-border bg-surface px-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20";

function move<T>(arr: T[], from: number, to: number): T[] {
  if (to < 0 || to >= arr.length) return arr;
  const next = arr.slice();
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it);
  return next;
}

/** Compact reorder/remove cluster, revealed on row hover or focus. */
function RowActions({
  disabled,
  onUp,
  onDown,
  onRemove,
}: {
  disabled: boolean;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  if (disabled) return null;
  const btn = "rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground";
  return (
    <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
      <button type="button" className={btn} aria-label={t("common.moveUp", "Move up")} onClick={onUp}>
        <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.5} />
      </button>
      <button type="button" className={btn} aria-label={t("common.moveDown", "Move down")} onClick={onDown}>
        <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.5} />
      </button>
      <button
        type="button"
        className="rounded p-1 text-muted-foreground hover:bg-danger/10 hover:text-danger"
        aria-label={t("common.remove", "Remove")}
        onClick={onRemove}
      >
        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
      </button>
    </div>
  );
}

/** The muted square glyph that marks an entry's kind. */
function KindGlyph({ Icon }: { Icon: LucideIcon }) {
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
      <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
    </span>
  );
}

function IconSelect({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <select
      aria-label={t("apps.navIcon", "Icon")}
      className={`${FIELD_CLS} w-32 shrink-0`}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{t("apps.navIconDefault", "Default icon")}</option>
      {NAV_ICON_NAMES.map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>
  );
}

/** Inline fields for a page/link entry (one clean row). */
function LeafFields({
  item,
  pages,
  disabled,
  onChange,
}: {
  item: PortalNavConfigItem;
  pages: NavEditorPage[];
  disabled: boolean;
  onChange: (patch: Partial<PortalNavConfigItem>) => void;
}) {
  const { t } = useTranslation();
  if (item.kind === "link") {
    return (
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        <Input
          className="h-8 min-w-[7rem] flex-1"
          aria-label={t("apps.navLabel", "Label")}
          placeholder={t("apps.navLabel", "Label")}
          value={item.label ?? ""}
          disabled={disabled}
          onChange={(e) => onChange({ label: e.target.value })}
        />
        <Input
          className="h-8 min-w-[9rem] flex-[2]"
          aria-label={t("apps.navUrl", "URL")}
          placeholder="https://…"
          value={item.url ?? ""}
          disabled={disabled}
          onChange={(e) => onChange({ url: e.target.value })}
        />
        <IconSelect value={item.icon ?? ""} disabled={disabled} onChange={(v) => onChange({ icon: v || null })} />
      </div>
    );
  }
  // page
  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
      <select
        aria-label={t("apps.navPage", "Page")}
        className={`${FIELD_CLS} min-w-[8rem] flex-1`}
        value={item.page ?? ""}
        disabled={disabled}
        onChange={(e) => onChange({ page: e.target.value })}
      >
        <option value="" disabled>
          {t("apps.navPickPage", "Select a page…")}
        </option>
        {pages.map((p) => (
          <option key={p.slug} value={p.slug}>
            {p.title}
          </option>
        ))}
      </select>
      <Input
        className="h-8 min-w-[6rem] flex-1"
        aria-label={t("apps.navLabelOverride", "Label override")}
        placeholder={t("apps.navLabelOptional", "Label (optional)")}
        value={item.label ?? ""}
        disabled={disabled}
        onChange={(e) => onChange({ label: e.target.value })}
      />
      <IconSelect value={item.icon ?? ""} disabled={disabled} onChange={(v) => onChange({ icon: v || null })} />
    </div>
  );
}

/** Subtle "+ add" affordance used at the top level and inside groups. */
function AddButton({ Icon, label, onClick }: { Icon: LucideIcon; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
      {label}
    </button>
  );
}

export function PortalNavEditor({
  value,
  pages,
  disabled,
  onChange,
}: {
  value: PortalNavConfigItem[];
  pages: NavEditorPage[];
  disabled: boolean;
  onChange: (items: PortalNavConfigItem[]) => void;
}) {
  const { t } = useTranslation();

  const setItem = (i: number, patch: Partial<PortalNavConfigItem>) =>
    onChange(value.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeItem = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const moveItem = (i: number, dir: -1 | 1) => onChange(move(value, i, i + dir));
  const add = (kind: PortalNavConfigItem["kind"]) =>
    onChange([
      ...value,
      kind === "group"
        ? { kind, label: "", children: [] }
        : kind === "link"
          ? { kind, label: "", url: "" }
          : { kind, page: pages[0]?.slug ?? "", label: "" },
    ]);
  const addAllPages = () =>
    onChange([...value, ...pages.map((p) => ({ kind: "page" as const, page: p.slug, label: "" }))]);

  // Child ops within a group (page/link only).
  const setChild = (gi: number, ci: number, patch: Partial<PortalNavConfigItem>) =>
    setItem(gi, {
      children: (value[gi].children ?? []).map((c, idx) => (idx === ci ? { ...c, ...patch } : c)),
    });
  const removeChild = (gi: number, ci: number) =>
    setItem(gi, { children: (value[gi].children ?? []).filter((_, idx) => idx !== ci) });
  const moveChild = (gi: number, ci: number, dir: -1 | 1) =>
    setItem(gi, { children: move(value[gi].children ?? [], ci, ci + dir) });
  const addChild = (gi: number, kind: "page" | "link") =>
    setItem(gi, {
      children: [
        ...(value[gi].children ?? []),
        kind === "link" ? { kind, label: "", url: "" } : { kind, page: pages[0]?.slug ?? "", label: "" },
      ],
    });

  // A single page/link row (used at top level and inside groups).
  const leafRow = (
    item: PortalNavConfigItem,
    onPatch: (patch: Partial<PortalNavConfigItem>) => void,
    actions: { onUp: () => void; onDown: () => void; onRemove: () => void },
  ) => (
    <div className="group flex items-center gap-2 rounded-md border border-border/60 bg-surface/40 px-2 py-1.5">
      <KindGlyph Icon={navIcon({ kind: item.kind, icon: item.icon })} />
      <LeafFields item={item} pages={pages} disabled={disabled} onChange={onPatch} />
      <RowActions disabled={disabled} {...actions} />
    </div>
  );

  return (
    <div className="space-y-2">
      {value.map((item, i) =>
        item.kind === "group" ? (
          <div key={i} className="group rounded-lg border border-border/60 bg-surface/30 p-2.5">
            {/* Group header */}
            <div className="flex items-center gap-2">
              <KindGlyph Icon={navIcon({ kind: "group" })} />
              <Input
                className="h-8 flex-1 font-medium"
                aria-label={t("apps.navGroupLabel", "Group label")}
                placeholder={t("apps.navGroupLabel", "Group label")}
                value={item.label ?? ""}
                disabled={disabled}
                onChange={(e) => setItem(i, { label: e.target.value })}
              />
              <RowActions
                disabled={disabled}
                onUp={() => moveItem(i, -1)}
                onDown={() => moveItem(i, 1)}
                onRemove={() => removeItem(i)}
              />
            </div>
            {/* Group children */}
            <div className="ml-3.5 mt-2 space-y-1.5 border-l border-border pl-3">
              {(item.children ?? []).map((child, ci) => (
                // Keyed by position — the SAME page may legitimately appear
                // more than once (duplicates are allowed), so a slug/content
                // key would wrongly merge two rows.
                <div key={ci}>
                  {leafRow(child, (patch) => setChild(i, ci, patch), {
                    onUp: () => moveChild(i, ci, -1),
                    onDown: () => moveChild(i, ci, 1),
                    onRemove: () => removeChild(i, ci),
                  })}
                </div>
              ))}
              {(item.children ?? []).length === 0 && (
                <p className="px-1 py-0.5 text-xs text-muted-foreground/70">
                  {t("apps.navGroupEmpty", "Empty group — add a page or link.")}
                </p>
              )}
              {!disabled && (
                <div className="flex items-center gap-1 pt-0.5">
                  <AddButton Icon={Plus} label={t("apps.navPage", "Page")} onClick={() => addChild(i, "page")} />
                  <AddButton Icon={Link2} label={t("apps.navLink", "Link")} onClick={() => addChild(i, "link")} />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div key={i}>
            {leafRow(item, (patch) => setItem(i, patch), {
              onUp: () => moveItem(i, -1),
              onDown: () => moveItem(i, 1),
              onRemove: () => removeItem(i),
            })}
          </div>
        ),
      )}

      {!disabled && (
        <div className="flex flex-wrap items-center gap-1 border-t border-border/60 pt-3">
          <AddButton Icon={FileText} label={t("apps.navAddPage", "Add page")} onClick={() => add("page")} />
          <AddButton Icon={Link2} label={t("apps.navAddLink", "Add link")} onClick={() => add("link")} />
          <AddButton Icon={FolderPlus} label={t("apps.navAddGroup", "Add group")} onClick={() => add("group")} />
          {pages.length > 0 && (
            <button
              type="button"
              onClick={addAllPages}
              className="ml-auto rounded-md px-2 py-1 text-xs font-medium text-accent hover:underline"
            >
              {t("apps.navAddAllPages", "Add all pages")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
